import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../lib/cn";

const fieldBase =
  "w-full rounded-xl border border-white/10 bg-white/[0.035] px-3.5 text-sm text-zinc-100 placeholder:text-zinc-600 transition-colors hover:border-white/[0.15] focus:border-violet-400/50 focus:bg-white/[0.05] focus:outline-none focus:ring-2 focus:ring-violet-500/15 disabled:cursor-not-allowed disabled:opacity-50";

export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <label
          htmlFor={htmlFor}
          className="text-[13px] font-medium text-zinc-300"
        >
          {label}
        </label>
        {hint && <span className="text-[11px] text-zinc-600">{hint}</span>}
      </div>
      {children}
      {error && (
        <p className="text-[12px] text-rose-300" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        fieldBase,
        "h-11",
        invalid && "border-rose-400/40 focus:border-rose-400/60",
        className,
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
});

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  invalid?: boolean;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea({ className, invalid, ...props }, ref) {
    return (
      <textarea
        ref={ref}
        className={cn(
          fieldBase,
          "min-h-24 resize-none py-3 leading-relaxed",
          invalid && "border-rose-400/40 focus:border-rose-400/60",
          className,
        )}
        aria-invalid={invalid || undefined}
        {...props}
      />
    );
  },
);
